import React from 'react';
import Icon from '../AppIcon';
import { cn } from '../../utils/cn';

const TeamNotificationPanel = ({ 
  notifications = [], 
  unreadCount = 0,
  onNotificationClick,
  onMarkAllRead,
  onClose 
}) => {
  const getPriorityIcon = (priority) => {
    const icons = {
      urgent: 'AlertCircle',
      high: 'AlertTriangle',
      medium: 'Info',
      low: 'MessageCircle'
    };
    return icons?.[priority] || 'Info';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      urgent: 'text-destructive',
      high: 'text-warning',
      medium: 'text-primary',
      low: 'text-muted-foreground'
    };
    return colors?.[priority] || 'text-muted-foreground';
  };

  const getNotificationTypeLabel = (type) => {
    const labels = {
      message_mention: 'Mention',
      conversation_shared: 'Shared',
      agent_assigned: 'Assignment',
      project_update: 'Update',
      document_uploaded: 'Document',
      team_collaboration: 'Team'
    };
    return labels?.[type] || 'Notification';
  };

  const formatTimeAgo = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date?.toLocaleDateString();
  };

  return (
    <div className="absolute right-0 top-full mt-2 w-96 bg-card border border-border rounded-lg shadow-lg z-50 max-h-[32rem] flex flex-col">
      {/* Header */}
      <div className="sticky top-0 bg-card border-b border-border px-4 py-3 flex items-center justify-between rounded-t-lg">
        <div className="flex items-center gap-2">
          <h3 className="font-semibold text-foreground">Notifications</h3>
          {unreadCount > 0 && (
            <span className="bg-primary text-primary-foreground text-xs rounded-full px-2 py-0.5">
              {unreadCount}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {unreadCount > 0 && (
            <button
              onClick={onMarkAllRead}
              className="text-xs text-primary hover:underline"
            >
              Mark all read
            </button>
          )}
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name="X" size={16} />
          </button>
        </div>
      </div>

      {/* Notifications List */}
      <div className="overflow-y-auto flex-1">
        {notifications?.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">
            <Icon name="Bell" size={48} className="mx-auto mb-3 opacity-50" />
            <p className="text-sm font-medium">No notifications</p>
            <p className="text-xs mt-1">You're all caught up!</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {notifications?.map(notification => (
              <button
                key={notification?.id}
                onClick={() => onNotificationClick?.(notification)}
                className={cn(
                  'w-full p-4 text-left hover:bg-muted/50 transition-colors',
                  !notification?.isRead && 'bg-primary/5 border-l-2 border-l-primary'
                )}
              >
                <div className="flex items-start gap-3">
                  {/* Priority Indicator */}
                  <div className={cn(
                    'flex-shrink-0 mt-1',
                    getPriorityColor(notification?.priority)
                  )}>
                    <Icon name={getPriorityIcon(notification?.priority)} size={18} />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <h4 className={cn(
                        'font-medium text-sm text-foreground',
                        !notification?.isRead && 'font-semibold'
                      )}>
                        {notification?.title}
                      </h4>
                      <span className="text-xs text-muted-foreground flex-shrink-0">
                        {formatTimeAgo(notification?.createdAt)}
                      </span>
                    </div>
                    
                    <p className="text-xs text-muted-foreground mb-2">
                      {notification?.message}
                    </p>

                    {/* Metadata */}
                    <div className="flex items-center gap-2">
                      <span className="text-xs bg-secondary text-secondary-foreground px-2 py-0.5 rounded">
                        {getNotificationTypeLabel(notification?.notificationType)}
                      </span>
                      {notification?.priority !== 'low' && (
                        <span className={cn(
                          'text-xs px-2 py-0.5 rounded capitalize',
                          notification?.priority === 'urgent' && 'bg-destructive/10 text-destructive',
                          notification?.priority === 'high' && 'bg-warning/10 text-warning',
                          notification?.priority === 'medium' && 'bg-primary/10 text-primary'
                        )}>
                          {notification?.priority}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Unread Indicator */}
                  {!notification?.isRead && (
                    <div className="w-2 h-2 bg-primary rounded-full flex-shrink-0 mt-2"></div>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      {notifications?.length > 0 && (
        <div className="sticky bottom-0 bg-card border-t border-border px-4 py-2 rounded-b-lg">
          <p className="text-xs text-center text-muted-foreground">
            Showing {notifications?.length} notification{notifications?.length !== 1 ? 's' : ''}
          </p>
        </div>
      )}
    </div>
  );
};

export default TeamNotificationPanel;